import { useEffect, useState } from 'react';
import { cn } from '@/utils/cn';
import {
  type ShopDiscount,
  type ShopDiscountInput,
  type DiscountType,
  type AppliesTo,
  listDiscounts,
  createDiscount,
  updateDiscount,
  deleteDiscount,
} from '@/services/shopAdminApi';
import { Plus, Trash2, Save, X, AlertCircle, CheckCircle2, Loader2, Percent, Tag, Truck } from 'lucide-react';

const EMPTY: ShopDiscountInput = {
  code: '',
  description: '',
  discount_type: 'percent',
  value: 0,
  min_order_amount: null,
  max_discount_amount: null,
  applies_to: 'all',
  product_ids: [],
  category_ids: [],
  usage_limit: null,
  is_active: true,
  starts_at: null,
  ends_at: null,
};

const TYPE_LABELS: Record<DiscountType, string> = {
  percent: 'Percentage off',
  fixed: 'Fixed amount off',
  free_shipping: 'Free shipping',
};

export function DiscountsTab() {
  const [items, setItems] = useState<ShopDiscount[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ShopDiscountInput>(EMPTY);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const data = await listDiscounts();
      setItems(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load discounts');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function startCreate() {
    setEditingId('new');
    setForm(EMPTY);
  }

  function startEdit(d: ShopDiscount) {
    setEditingId(d.id);
    setForm({
      code: d.code,
      description: d.description || '',
      discount_type: d.discount_type,
      value: d.value,
      min_order_amount: d.min_order_amount,
      max_discount_amount: d.max_discount_amount,
      applies_to: d.applies_to,
      product_ids: d.product_ids || [],
      category_ids: d.category_ids || [],
      usage_limit: d.usage_limit,
      is_active: d.is_active,
      starts_at: d.starts_at,
      ends_at: d.ends_at,
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(EMPTY);
  }

  function update<K extends keyof ShopDiscountInput>(key: K, value: ShopDiscountInput[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    setSaving(true);
    setMessage(null);
    try {
      if (editingId === 'new') {
        await createDiscount(form);
        setMessage({ type: 'success', text: 'Discount created.' });
      } else if (editingId) {
        await updateDiscount(editingId, form);
        setMessage({ type: 'success', text: 'Discount updated.' });
      }
      setEditingId(null);
      setForm(EMPTY);
      await load();
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this discount?')) return;
    try {
      await deleteDiscount(id);
      await load();
      setMessage({ type: 'success', text: 'Deleted.' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || 'Delete failed.' });
    }
  }

  if (loading) return <div className="p-8 text-center text-[#6B7B6E]">Loading discounts…</div>;
  if (error) return <div className="p-6 text-red-600">{error}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#1A2E12]">Discounts</h2>
        <button
          onClick={startCreate}
          disabled={editingId === 'new'}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#BDD630] text-[#1A2E12] rounded-lg text-sm font-semibold hover:bg-[#96c23d] disabled:opacity-50"
        >
          <Plus className="w-4 h-4" /> Add Discount
        </button>
      </div>

      {message && (
        <div className={cn(
          'flex items-start gap-2 rounded-lg px-4 py-3 text-sm',
          message.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
        )}>
          {message.type === 'success' ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <AlertCircle className="w-4 h-4 mt-0.5" />}
          {message.text}
        </div>
      )}

      {editingId === 'new' && (
        <DiscountForm form={form} update={update} saving={saving} onSave={handleSave} onCancel={cancelEdit} />
      )}

      <div className="space-y-3">
        {items.length === 0 && <p className="text-sm text-[#6B7B6E]">No discounts yet.</p>}
        {items.map(d => (
          <div key={d.id} className="bg-white rounded-xl border border-[#D9B38C]/20 overflow-hidden">
            {editingId === d.id ? (
              <DiscountForm form={form} update={update} saving={saving} onSave={handleSave} onCancel={cancelEdit} />
            ) : (
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center shrink-0',
                    d.discount_type === 'free_shipping' ? 'bg-[#567D38]/10 text-[#567D38]' : d.discount_type === 'percent' ? 'bg-[#BDD630]/20 text-[#1A2E12]' : 'bg-[#E07A5F]/20 text-[#E07A5F]'
                  )}>
                    {d.discount_type === 'free_shipping' ? <Truck className="w-5 h-5" /> : d.discount_type === 'percent' ? <Percent className="w-5 h-5" /> : <Tag className="w-5 h-5" />}
                  </div>
                  <div>
                    <p className="font-semibold text-[#1A2E12]">{d.code}</p>
                    <p className="text-sm text-[#6B7B6E]">
                      {TYPE_LABELS[d.discount_type]}
                      {d.discount_type === 'percent' && ` · ${d.value}%`}
                      {d.discount_type === 'fixed' && ` · R${d.value}`}
                      {d.min_order_amount ? ` · min R${d.min_order_amount}` : ''}
                      {d.max_discount_amount ? ` · max R${d.max_discount_amount}` : ''}
                      {d.usage_limit ? ` · limit ${d.usage_count}/${d.usage_limit}` : ''}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2 text-xs text-[#6B7B6E]">
                      <span className="px-2 py-1 bg-[#F7F5EE] rounded">{d.applies_to}</span>
                      <span className={cn('px-2 py-1 rounded', d.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600')}>{d.is_active ? 'Active' : 'Inactive'}</span>
                      {d.starts_at && <span className="px-2 py-1 bg-[#F7F5EE] rounded">from {d.starts_at.slice(0, 10)}</span>}
                      {d.ends_at && <span className="px-2 py-1 bg-[#F7F5EE] rounded">until {d.ends_at.slice(0, 10)}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => startEdit(d)} className="p-2 text-[#6B7B6E] hover:text-[#1A2E12] hover:bg-[#F7F5EE] rounded-lg"><Save className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(d.id)} className="p-2 text-[#6B7B6E] hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function DiscountForm({
  form,
  update,
  saving,
  onSave,
  onCancel,
}: {
  form: ShopDiscountInput;
  update: <K extends keyof ShopDiscountInput>(key: K, value: ShopDiscountInput[K]) => void;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="bg-[#F7F5EE] rounded-xl p-4 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input label="Code" value={form.code} onChange={v => update('code', v.toUpperCase())} />
        <Select label="Type" value={form.discount_type} onChange={v => update('discount_type', v as DiscountType)} options={[{ value: 'percent', label: 'Percentage off' }, { value: 'fixed', label: 'Fixed amount off' }, { value: 'free_shipping', label: 'Free shipping' }]} />
        {form.discount_type !== 'free_shipping' && (
          <Input label="Value" type="number" value={String(form.value)} onChange={v => update('value', parseFloat(v || '0'))} />
        )}
        <Input label="Description" value={form.description || ''} onChange={v => update('description', v)} />
        <Input label="Min order amount" type="number" value={form.min_order_amount != null ? String(form.min_order_amount) : ''} onChange={v => update('min_order_amount', v ? parseFloat(v) : null)} />
        <Input label="Max discount amount" type="number" value={form.max_discount_amount != null ? String(form.max_discount_amount) : ''} onChange={v => update('max_discount_amount', v ? parseFloat(v) : null)} />
        <Input label="Usage limit" type="number" value={form.usage_limit != null ? String(form.usage_limit) : ''} onChange={v => update('usage_limit', v ? parseInt(v, 10) : null)} />
        <Select label="Applies to" value={form.applies_to} onChange={v => update('applies_to', v as AppliesTo)} options={[{ value: 'all', label: 'All products' }, { value: 'products', label: 'Specific products' }, { value: 'categories', label: 'Specific categories' }]} />
        <Input label="Product IDs (comma-separated)" value={(form.product_ids || []).join(', ')} onChange={v => update('product_ids', v.split(',').map(s => s.trim()).filter(Boolean).map(Number))} />
        <Input label="Category IDs (comma-separated)" value={(form.category_ids || []).join(', ')} onChange={v => update('category_ids', v.split(',').map(s => s.trim()).filter(Boolean).map(Number))} />
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-[#1A2E12]">
            <input type="checkbox" checked={form.is_active} onChange={e => update('is_active', e.target.checked)} />
            Active
          </label>
        </div>
        <Input label="Starts at" type="datetime-local" value={form.starts_at ? form.starts_at.slice(0, 16) : ''} onChange={v => update('starts_at', v ? new Date(v).toISOString() : null)} />
        <Input label="Ends at" type="datetime-local" value={form.ends_at ? form.ends_at.slice(0, 16) : ''} onChange={v => update('ends_at', v ? new Date(v).toISOString() : null)} />
      </div>
      <div className="flex items-center justify-end gap-2">
        <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-[#6B7B6E] hover:bg-white rounded-lg"><X className="w-4 h-4 inline mr-1" /> Cancel</button>
        <button onClick={onSave} disabled={saving} className="px-4 py-2 bg-[#BDD630] text-[#1A2E12] rounded-lg text-sm font-semibold hover:bg-[#96c23d] disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : <Save className="w-4 h-4 inline mr-1" />} Save
        </button>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = 'text' }: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-[#6B7B6E]">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-[#D9B38C]/30 text-sm focus:outline-none focus:ring-2 focus:ring-[#BDD630]"
      />
    </div>
  );
}

function Select({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-[#6B7B6E]">{label}</label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-[#D9B38C]/30 text-sm focus:outline-none focus:ring-2 focus:ring-[#BDD630] bg-white"
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
